import type { OnDestroy, OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  output,
  inject,
  input,
} from '@angular/core';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { Analytics } from '@core/analytics-types';
import { TranslatePipe } from '@core/i18n-legacy';
import { IntegrationItemComponent } from '@core/integrations-smart-ui';
import {
  OAuthAuthorizeActions,
  OAuthClientAuthActions,
  OAuthCountersActions,
} from '@core/oauth-data';
import { IconComponent, RtStatefulComponent, WindowRef } from '@core/user-interface-legacy';

type State = {
  deauthorizeLoading: boolean;
};

@Component({
  selector: 'rt-zapier-integration',
  templateUrl: './zapier-integration.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IntegrationItemComponent, IconComponent, TranslatePipe],
})
export class ZapierIntegrationComponent
  extends RtStatefulComponent<State>
  implements OnInit, OnDestroy
{
  private readonly analytics = inject(Analytics);
  private readonly windowRef = inject(WindowRef);
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  protected readonly cdRef: ChangeDetectorRef;

  public readonly enabled = input<boolean>(true);
  public readonly loading = input<boolean>(false);
  public readonly active = input<boolean>(false);
  public readonly clientId = input<string | null>(null);
  public readonly teamIntegrationsCount = input<number>(0);
  public statusChange = output<void>();

  private ngOnDestroy$ = new Subject<void>();

  constructor() {
    const cdRef = inject(ChangeDetectorRef);

    super(cdRef);
    this.cdRef = cdRef;

    this.createState({
      deauthorizeLoading: false,
    });
  }

  public ngOnInit() {
    this.actions$
      .pipe(ofType(OAuthAuthorizeActions.deauthorizeSuccess), takeUntil(this.ngOnDestroy$))
      .subscribe(() => {
        this.setState({ deauthorizeLoading: false });
        this.reloadAuthorizedClients();
      });

    this.actions$
      .pipe(ofType(OAuthAuthorizeActions.deauthorizeFailed), takeUntil(this.ngOnDestroy$))
      .subscribe(() => {
        this.setState({ deauthorizeLoading: false });

        alert('We had some problems while trying to deauthorize Zapier. Please, try again.');
      });
  }

  public ngOnDestroy() {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public reloadAuthorizedClients(): void {
    this.store.dispatch(OAuthClientAuthActions.fetch());
    this.store.dispatch(OAuthCountersActions.fetch());
  }

  public authorize(): void {
    this.analytics.track('[Settings] IntegrationsZapierIntegrateButtonClick');
    this.windowRef.open('https://zapier.com/apps/recruitee/integrations', '_blank');
  }

  public deauthorize(): void {
    this.setState({ deauthorizeLoading: true });
    this.store.dispatch(
      OAuthAuthorizeActions.deauthorize({ payload: { clientId: this.clientId()! } }),
    );
  }
}
